import { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  useForm,
  SupportedCurrencies,
  FieldData,
  TextField,
  Select,
  sanitizeToNumeric,
  PortalSubAccount,
  SubAccountsRequest,
  SubAccountsResponse,
  formattedCurrency,
} from 'vopay';
import { AccountTransferRequest } from './types';
import { resolveSpendableBalance } from '../resolveSpendableBalance';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import useAccountBalance from '@/hooks/useApi/useAccountBalance';
import useSubAccounts from '@/hooks/useApi/useSubAccounts';
import useFetchPortalSubAccounts from '@/hooks/useApi/useFetchPortalSubAccounts';

const SUB_ACCOUNTS_LIMIT = 50;
const SEARCH_MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 300;

type AccountTransferProps = {
  currency: SupportedCurrencies;
  token: string;
  accountId?: string;
};

export function useAccountTransfer() {
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const { response } = useClientData();
  const { state: accountTransferState } = useLocation<AccountTransferProps>();
  const accountIdProp = accountTransferState?.accountId;
  const currencyProp = accountTransferState?.currency;
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.AccountTransfer' });

  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { fetchSubAccounts, transferAccountTo } = useSubAccounts();
  const { balance, fetchBalance } = useAccountBalance();
  const [isUnderReview, setIsUnderReview] = useState(false);

  const currency = currencyProp || (response.Country === 'US' ? 'USD' : 'CAD');
  const selectedAccount = accountIdProp ?? '';

  const setFieldValueRef = useRef<
    ((field: string, value: string | undefined, shouldValidate?: boolean) => void) | null
  >(null);

  const fromTokenRef = useRef<string | undefined>(undefined);

  const { fieldDataList, isFromResolved } = useFormConfig(
    response.AccountID,
    response.AccountName,
    balance?.AvailableFunds?.toString() ?? '0.00',
    response.clientAccountsEnabled,
    fetchSubAccounts,
    selectedAccount,
    currency,
    setFieldValueRef,
    fromTokenRef,
    () =>
      alertDialog.error(
        tGlobal('error', { defaultValue: 'Error' }),
        t('resolveBalanceErrorMessage', {
          defaultValue: 'Failed to resolve the available balance for the selected "From" account',
        }),
      ),
  );

  const { renderFormElements, ctx } = useForm<AccountTransferRequest>({
    config: fieldDataList,
    initialValues: {
      Currency: currency,
      From: selectedAccount,
      To: '',
      Amount: 0,
    },
    onSubmit: async (serializedData) => {
      const redirectToTransferDetails = (transactionId: string) => {
        history.push({
          pathname: `/transactions-history/details/AccountTransfer/${transactionId}/${ctx.values.Currency}/`,
          state: {
            transactionId,
            activeModule: 'accountTransfer',
            hasNewTransactionButton: true,
          },
        });
      };

      try {
        setDataFetchingInfo(true);

        // NOTE: Faizal says we should not use /account/transfer-from anymore.
        // Workaround for transfer-from: instead of pulling into the parent account
        // and pushing back out (two hops, two transactions), run a single
        // transfer-to authenticated as the "From" account via its own JWT.
        const fromMain = response.AccountID === ctx.values.From;
        const bearer = fromMain ? undefined : fromTokenRef.current;

        // Without a bearer the call would silently run as the logged-in parent
        // and debit the wrong account — never let that reach the API.
        if (!fromMain && !bearer) {
          throw new Error(
            t('missingFromTokenErrorMessage', {
              defaultValue: 'The selected "From" account could not be verified. Please re-select it and try again.',
            }),
          );
        }

        const res = await transferAccountTo(
          {
            Amount: serializedData.Amount,
            RecipientAccountID: ctx.values.To,
            Currency: ctx.values.Currency,
          },
          bearer,
        );

        redirectToTransferDetails(res.TransactionID);
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : t('transferErrorMessage', { defaultValue: 'Failed sub-account transfer' });

        alertDialog.error(tGlobal('error', { defaultValue: 'Error' }), msg);
      } finally {
        setDataFetchingInfo(false);
      }
    },
    isReviewing: isUnderReview,
  });

  setFieldValueRef.current = ctx.setFieldValue;

  useEffect(() => {
    const getBalance = async () => {
      try {
        await fetchBalance(ctx.values.Currency || 'CAD');
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : t('balanceErrorMessage', { defaultValue: 'Failed to fetch the account balance' });

        alertDialog.error(tGlobal('error', { defaultValue: 'Error' }), msg);
      }
    };

    getBalance();
  }, [ctx.values.Currency]);

  return {
    ctx,
    renderFormElements: () => renderFormElements({ config: fieldDataList }),
    isUnderReview,
    setIsUnderReview,
    isFromResolved,
  };
}

function useFormConfig(
  mainAccountId: string,
  mainAccountName: string,
  mainAccountBalance: string,
  mainClientAccountsEnabled: boolean,
  fetchAccountDetail: (params: SubAccountsRequest) => Promise<SubAccountsResponse>,
  initialFromAccountId: string,
  initialCurrency: string,
  setFieldValueRef: React.RefObject<
    ((field: string, value: string | undefined, shouldValidate?: boolean) => void) | null
  >,
  fromTokenRef: React.RefObject<string | undefined>,
  onFromResolveError: () => void,
): { fieldDataList: FieldData<AccountTransferRequest>[]; isFromResolved: boolean } {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.AccountTransfer' });
  const [selectedCurrency, setSelectedCurrency] = useState(initialCurrency);
  const [selectedDebitorAccountID, setSelectedDebitorAccountID] = useState(initialFromAccountId);
  const [isFromResolved, setIsFromResolved] = useState(false);
  const [selectedRecipientAccountID, setSelectedRecipientAccountID] = useState('');
  const [fromLabel, setFromLabel] = useState('');
  const [toLabel, setToLabel] = useState('');
  const [fromSearchText, setFromSearchText] = useState('');
  const [toSearchText, setToSearchText] = useState('');

  const fromList = useFetchPortalSubAccounts();
  const toList = useFetchPortalSubAccounts();

  const fromResolveRequestId = useRef(0);
  const toResolveRequestId = useRef(0);

  useEffect(() => {
    const loadInitial = async () => {
      try {
        await Promise.all([
          fromList.fetchPortalSubAccounts({ Limit: SUB_ACCOUNTS_LIMIT }),
          toList.fetchPortalSubAccounts({ Limit: SUB_ACCOUNTS_LIMIT }),
        ]);
      } catch (error) {
        console.error(error);
      }
    };

    void loadInitial();
  }, []);

  async function resolveAccount(accountId: string, currencySelected: string) {
    if (accountId === mainAccountId) {
      const balance = await resolveSpendableBalance({
        clientAccountsEnabled: mainClientAccountsEnabled,
        currency: currencySelected,
        fallbackBalance: mainAccountBalance || '0.00',
      });

      return {
        label: `${mainAccountName} ($${formattedCurrency(balance)})`,
        balance,
        token: undefined,
      };
    }

    const res = await fetchAccountDetail({ ActiveOnly: true, ShowToken: true, Limit: 1, SubaccountID: accountId });
    const found = res.Subaccounts.find((account) => account.AccountID === accountId);

    if (!found) throw new Error(`Account ${accountId} not found`);
    // The type says Token is always set, but it only comes back when ShowToken
    // is honoured — without it the balance fetch below would run as the parent.
    if (!found.Token) throw new Error(`No token returned for sub-account ${accountId}`);

    const aggregateBalance =
      Object.values(found.Balances || {}).find((bal) => bal.Currency === currencySelected)?.AvailableBalance || '0.00';

    const balance = await resolveSpendableBalance({
      clientAccountsEnabled: found.ClientAccountsEnabled,
      currency: currencySelected,
      fallbackBalance: aggregateBalance,
      bearer: found.Token,
    });

    // Match the dropdown option label, which uses the list endpoint's
    return { label: `${found.AccountName} ($${formattedCurrency(balance)})`, balance, token: found.Token };
  }

  useEffect(() => {
    if (!selectedDebitorAccountID) {
      setFromLabel('');
      fromTokenRef.current = undefined;
      setIsFromResolved(false);
      setFieldValueRef.current?.('FromBalance', undefined, false);

      return;
    }

    const requestId = ++fromResolveRequestId.current;
    fromTokenRef.current = undefined;
    setIsFromResolved(false);

    resolveAccount(selectedDebitorAccountID, selectedCurrency)
      .then(({ label, balance, token }) => {
        if (requestId !== fromResolveRequestId.current) return;
        setFromLabel(label);
        fromTokenRef.current = token;
        setIsFromResolved(true);
        setFieldValueRef.current?.('FromBalance', balance, false);
      })
      .catch((error) => {
        if (requestId !== fromResolveRequestId.current) return;
        console.error(error);
        setFromLabel('');
        fromTokenRef.current = undefined;
        setIsFromResolved(false);
        setFieldValueRef.current?.('FromBalance', undefined, false);
        onFromResolveError();
      });
  }, [
    selectedDebitorAccountID,
    selectedCurrency,
    mainAccountBalance,
    mainAccountId,
    mainAccountName,
    mainClientAccountsEnabled,
  ]);

  // Same for the "To" side — display only, it carries no validation ceiling.
  useEffect(() => {
    if (!selectedRecipientAccountID) {
      setToLabel('');

      return;
    }

    const requestId = ++toResolveRequestId.current;

    resolveAccount(selectedRecipientAccountID, selectedCurrency)
      .then(({ label }) => {
        if (requestId !== toResolveRequestId.current) return;
        setToLabel(label);
      })
      .catch((error) => {
        if (requestId !== toResolveRequestId.current) return;
        console.error(error);
        setToLabel('');
      });
  }, [
    selectedRecipientAccountID,
    selectedCurrency,
    mainAccountBalance,
    mainAccountId,
    mainAccountName,
    mainClientAccountsEnabled,
  ]);

  const CurrencyOptions = [
    { label: 'CAD', value: 'CAD' },
    { label: 'USD', value: 'USD' },
  ];

  const fromAccountsOptions = buildSubAccountOptions(
    fromList.subAccounts,
    selectedRecipientAccountID,
    selectedRecipientAccountID === mainAccountId,
    fromSearchText,
  );

  const toAccountsOptions = buildSubAccountOptions(
    toList.subAccounts,
    selectedDebitorAccountID,
    selectedDebitorAccountID === mainAccountId,
    toSearchText,
  );

  const fieldDataList: FieldData<AccountTransferRequest>[] = [
    {
      label: () => t('currency', { defaultValue: 'Currency' }),
      fieldName: 'Currency',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            ctx.setFieldValue('Currency', e.target.value);
            setSelectedCurrency(e.target.value);
          },
        });

        return <Select version="2" options={CurrencyOptions} {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('amount', { defaultValue: 'Amount' }),
      fieldName: 'Amount',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: AccountTransferRequest) => Number(sanitizeToNumeric(String(values.Amount))),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" preset="currency" {...fieldData} />;
      },
      validate: (ctx) => {
        const availableBalance = ctx.values.FromBalance;

        return Yup.string()
          .test(
            'amount-min-validation',
            t('amountValidation', { defaultValue: 'This value should be greater than or equal to $0.01.' }),
            (value) => {
              const amountValidation = Number(value) <= 0.0;

              return !amountValidation;
            },
          )
          .test(
            'amount-max-validation',
            `${t('maxAmountValidation', { defaultValue: 'Insufficient funds. Maximum allowed is:' })} $${String(availableBalance ?? '0.00')}`,
            (value) => {
              if (availableBalance === undefined) return true;
              const max = Number(availableBalance);
              if (Number.isNaN(max)) return true;

              return Number(sanitizeToNumeric(value)) <= max;
            },
          )
          .required();
      },
    },
    {
      label: () => t('from', { defaultValue: 'From' }),
      fieldName: 'From',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <Select
            version="2"
            sort={false}
            filterable
            loading={fromList.fetching}
            placeholder={t('selectPlaceholder', { defaultValue: 'Please Select...' })}
            emptyFilterMsg={t('noSubAccounts', { defaultValue: 'No sub-accounts found' })}
            options={fromAccountsOptions}
            onFilter={(e) => {
              e.preventDefault();
              setFromSearchText(e.filter);
              if (e.filter.length > 0 && e.filter.length < SEARCH_MIN_CHARS) return;

              e.debounce(
                () => fromList.fetchPortalSubAccounts({ SearchText: e.filter, Limit: SUB_ACCOUNTS_LIMIT }),
                SEARCH_DEBOUNCE_MS,
              );
            }}
            renderSelectedValue={(_, { value, Placeholder }) => {
              if (!value) return <Placeholder />;

              return <>{fromLabel || value}</>;
            }}
            {...fieldData}
            onChange={(e, { value }) => {
              fieldData.onChange(e);
              setSelectedDebitorAccountID(value);
            }}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('to', { defaultValue: 'To' }),
      fieldName: 'To',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return (
          <Select
            version="2"
            sort={false}
            filterable
            loading={toList.fetching}
            placeholder={t('selectPlaceholder', { defaultValue: 'Please Select...' })}
            emptyFilterMsg={t('noSubAccounts', { defaultValue: 'No sub-accounts found' })}
            options={toAccountsOptions}
            onFilter={(e) => {
              e.preventDefault();
              setToSearchText(e.filter);
              if (e.filter.length > 0 && e.filter.length < SEARCH_MIN_CHARS) return;

              e.debounce(
                () => toList.fetchPortalSubAccounts({ SearchText: e.filter, Limit: SUB_ACCOUNTS_LIMIT }),
                SEARCH_DEBOUNCE_MS,
              );
            }}
            renderSelectedValue={(_, { value, Placeholder }) => {
              if (!value) return <Placeholder />;

              return <>{toLabel || value}</>;
            }}
            {...fieldData}
            onChange={(e, { value }) => {
              fieldData.onChange(e);
              setSelectedRecipientAccountID(value);
            }}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
  ];

  return { fieldDataList, isFromResolved };

  function buildSubAccountOptions(
    lists: PortalSubAccount[],
    excludeAccountId: string,
    removeMain: boolean,
    searchText: string,
  ): { label: string; value: string }[] {
    const isSearching = searchText.length >= SEARCH_MIN_CHARS;
    const mainMatches = mainAccountName.toLowerCase().includes(searchText.toLowerCase());

    const initial: { label: string; value: string }[] =
      removeMain || (isSearching && !mainMatches) ? [] : [{ label: mainAccountName, value: mainAccountId }];

    return [
      ...initial,
      ...lists
        .filter((data) => data.APIAccountID !== excludeAccountId)
        .map((data) => ({ label: data.AccountName, value: data.APIAccountID })),
    ];
  }
}
